/**
 * CMS-06 — playlists table plus the editing drawer: names in both languages, image, status and
 * the ordered song picker with search, drag ordering and the total count (docs/06 §2.5).
 *
 * The songs of a list are saved with their own call (`PUT /cms/playlists/:id/songs`, a full
 * replacement) so ordering never depends on the rest of the form; a new playlist posts both at
 * once (`POST /cms/playlists` takes `songIds`).
 */
import type {
  CmsPlaylistDTO,
  CmsPlaylistResponse,
  CmsPlaylistSongDTO,
  CmsPlaylistsQueryInput,
  CmsPlaylistsResponse,
  CmsSongsResponse,
  PlaylistKind,
  PublishStatus,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { EntitySearch, type SearchCandidate } from '../components/editorial/EntitySearch';
import { OrderedList, swatch } from '../components/editorial/OrderedList';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Drawer } from '../components/ui/drawer';
import { Field, Input, Label, Select, Textarea } from '../components/ui/field';
import { Badge } from '../components/ui/panel';
import { useToast } from '../components/ui/toast';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatNumber } from '../lib/format';
import { appendUnique, move, removeAt } from '../lib/order';
import { uploadFile } from '../lib/upload';

export interface PlaylistFilters {
  q: string;
  status: PublishStatus | '';
  kind: PlaylistKind | '';
}

export const EMPTY_PLAYLIST_FILTERS: PlaylistFilters = { q: '', status: '', kind: '' };

const SORT_IDS = new Set(['name', 'status', 'created_at', 'updated_at']);

export function toPlaylistsQuery(
  filters: PlaylistFilters,
  page: number,
  sorting: SortingState,
): CmsPlaylistsQueryInput {
  const query: CmsPlaylistsQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.status) query.status = filters.status;
  if (filters.kind) query.kind = filters.kind;
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

/** One row of the picker; a new playlist keeps them in memory until it is created. */
interface PickedSong {
  songId: string;
  title: string;
  artistNames: string;
  imageUrl: string | null;
  status: CmsPlaylistSongDTO['status'];
}

async function searchSongs(term: string): Promise<SearchCandidate[]> {
  const page: CmsSongsResponse = await api.cmsSongs({ query: { q: term, pageSize: 8 } });
  return page.items.map((song) => ({
    id: song.id,
    title: song.title,
    subtitle: song.artists.map((artist) => artist.name).join(', '),
    imageUrl: song.imageUrl,
    tag: song.status === 'published' ? undefined : song.status,
  }));
}

function PlaylistDrawer({
  playlist,
  onClose,
}: {
  playlist: CmsPlaylistDTO | 'new';
  onClose: () => void;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const isNew = playlist === 'new';
  const current = isNew ? null : playlist;

  const detail = useQuery<CmsPlaylistResponse>({
    queryKey: ['cms', 'playlist', current?.id],
    queryFn: () => api.cmsPlaylist({ params: { id: current!.id } }),
    enabled: !isNew,
  });

  const [draft, setDraft] = useState(() => ({
    nameEs: current ? current.name.es : '',
    nameEn: current ? current.name.en : '',
    description: current?.description ?? '',
    imagePath: current?.imagePath ?? null,
    status: current?.status ?? ('draft' as PublishStatus),
  }));
  const [newSongs, setNewSongs] = useState<PickedSong[]>([]);
  const [songs, setSongs] = useState<PickedSong[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // What the operator just picked, shown right away (the saved URL only exists after Save).
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  // The saved order is the server's until the operator touches it (no state sync effects).
  const serverSongs: PickedSong[] = (detail.data?.songs ?? []).map((song) => ({
    songId: song.songId,
    title: song.title,
    artistNames: song.artistNames,
    imageUrl: song.imageUrl,
    status: song.status,
  }));
  const list: PickedSong[] = isNew ? newSongs : (songs ?? serverSongs);
  /** One setter for both cases: a new playlist edits `newSongs`, an existing one `songs`. */
  const setList = (update: (entries: PickedSong[]) => PickedSong[]) => {
    const next = update(list);
    if (isNew) setNewSongs(next);
    else setSongs(next);
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['cms', 'playlists'] });
    if (current) void queryClient.invalidateQueries({ queryKey: ['cms', 'playlist', current.id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const name = { es: draft.nameEs.trim(), en: draft.nameEn.trim() || draft.nameEs.trim() };
      const body = {
        name,
        description: draft.description.trim() ? draft.description.trim() : null,
        imagePath: draft.imagePath,
        status: draft.status,
      };
      if (isNew) {
        return api.cmsPlaylistCreate({
          body: { ...body, songIds: newSongs.map((song) => song.songId) },
        });
      }
      const updated = await api.cmsPlaylistPatch({ params: { id: current!.id }, body });
      if (songs) {
        return api.cmsPlaylistSongsPut({
          params: { id: current!.id },
          body: { songIds: songs.map((song) => song.songId) },
        });
      }
      return updated;
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: () => setError(t('common:states.error')),
  });

  const confirm = useConfirm();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: () => api.cmsPlaylistDelete({ params: { id: current!.id } }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: () => setError(t('cms:playlists.deleteBlocked')),
  });

  /** A playlist is editorial content a room can be shown, so deleting it asks first (UX-211). */
  const onDelete = async () => {
    const ok = await confirm({
      title: t('cms:confirm.deletePlaylist.title', {
        name: current!.name.es || current!.name.en,
      }),
      body: t('cms:confirm.deletePlaylist.body'),
      confirmLabel: t('cms:confirm.deletePlaylist.confirm'),
      tone: 'danger',
    });
    if (ok) remove.mutate();
  };

  /**
   * QA 2026-09-15 **UX-534** — «Vaciar lista» took a list from 2 songs to 0 on one click with no
   * dialog, while deleting the same list asks. It is the same alertdialog now; the draft is only
   * written by «Guardar», which the body says.
   */
  const onClear = async () => {
    if (list.length === 0) return;
    const ok = await confirm({
      title: t('cms:confirm.clearPlaylist.title'),
      body: t('cms:confirm.clearPlaylist.body', { count: list.length }),
      confirmLabel: t('cms:confirm.clearPlaylist.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    const previous = list;
    setList(() => []);
    toast({
      message: t('cms:undo.playlistCleared'),
      tone: 'danger',
      action: { label: t('cms:undo.action'), onAction: () => setList(() => previous) },
    });
  };

  const onImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      // A playlist that does not exist yet has no id to own the file: it goes up as a loose
      // image (`catalog-images/misc/…`) and its key is saved with the playlist.
      const { key } = await uploadFile(file, {
        kind: 'image',
        ...(isNew ? {} : { owner: 'playlist' as const, ownerId: current!.id }),
      });
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });
      setDraft((d) => ({ ...d, imagePath: key }));
    } catch {
      setError(t('cms:songs.editor.uploadFailed'));
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        isNew
          ? t('cms:playlists.create')
          : `${t('cms:playlists.edit')} · ${current!.name.es || current!.name.en}`
      }
      subtitle={isNew ? undefined : t('cms:playlists.meta', { id: current!.id })}
      footer={
        <>
          {!isNew && (
            <Button
              variant="link"
              size="sm"
              className="mr-auto text-danger"
              disabled={remove.isPending}
              onClick={() => void onDelete()}
            >
              {t('common:actions.delete')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={draft.nameEs.trim().length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('cms:playlists.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('cms:playlists.nameEs')}>
            {(id) => (
              <Input
                id={id}
                value={draft.nameEs}
                onChange={(e) => setDraft((d) => ({ ...d, nameEs: e.target.value }))}
              />
            )}
          </Field>
          <Field label={t('cms:playlists.nameEn')}>
            {(id) => (
              <Input
                id={id}
                value={draft.nameEn}
                onChange={(e) => setDraft((d) => ({ ...d, nameEn: e.target.value }))}
              />
            )}
          </Field>
        </div>

        <div className="flex items-end gap-3">
          {draft.imagePath || current?.imageUrl ? (
            <img
              src={preview ?? current?.imageUrl ?? undefined}
              alt=""
              className="size-14 rounded-lg object-cover"
              style={{ background: swatch(current?.id ?? 'new') }}
            />
          ) : (
            <span
              aria-hidden="true"
              className="size-14 rounded-lg"
              style={{ background: swatch(current?.id ?? 'new') }}
            />
          )}
          <Field label={t('cms:playlists.image')} className="flex-1">
            {(id) => <Input id={id} value={draft.imagePath ?? ''} readOnly disabled />}
          </Field>
          <div>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              className="hidden"
              data-testid="playlist-image-input"
              onChange={(event) => void onImage(event)}
            />
            <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
              {t('cms:playlists.uploadImage')}
            </Button>
          </div>
        </div>

        <Field label={t('cms:playlists.description')}>
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          )}
        </Field>

        <Field label={t('cms:playlists.status')}>
          {(id) => (
            <Select
              id={id}
              value={draft.status}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as PublishStatus }))}
            >
              <option value="draft">{t('cms:status.draft')}</option>
              <option value="published">{t('cms:status.published')}</option>
            </Select>
          )}
        </Field>

        <div className="flex items-center justify-between pt-2">
          <h3 className="text-[13px] font-bold text-ink">{t('cms:playlists.songs')}</h3>
          <span className="flex items-center gap-2">
            <Badge tone="pink">{t('cms:playlists.songsCount', { count: list.length })}</Badge>
            <Button
              variant="link"
              size="sm"
              disabled={list.length === 0}
              onClick={() => void onClear()}
            >
              {t('cms:playlists.clear')}
            </Button>
          </span>
        </div>

        <EntitySearch
          kind="songs"
          placeholder={t('cms:playlists.pickSongs')}
          search={searchSongs}
          exclude={list.map((song) => song.songId)}
          onPick={(candidate) =>
            setList((entries) =>
              appendUnique(
                entries,
                {
                  songId: candidate.id,
                  title: candidate.title,
                  artistNames: candidate.subtitle ?? '',
                  imageUrl: candidate.imageUrl ?? null,
                  status: candidate.tag ? 'draft' : 'published',
                },
                (a, b) => a.songId === b.songId,
              ),
            )
          }
        />

        <OrderedList
          data-testid="playlist-songs"
          emptyLabel={t('cms:playlists.empty')}
          entries={list.map((song) => ({
            key: song.songId,
            title: song.title,
            subtitle: song.artistNames,
            imageUrl: song.imageUrl,
            tag: song.status === 'published' ? undefined : t('cms:status.draft'),
            warn: song.status !== 'published',
          }))}
          onMove={(from, to) => setList((entries) => move(entries, from, to))}
          onRemove={(index) => setList((entries) => removeAt(entries, index))}
        />

        {error && (
          <p className="text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

export function Playlists() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');

  const [filters, setFilters] = useState<PlaylistFilters>(EMPTY_PLAYLIST_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);
  const [selected, setSelected] = useState<CmsPlaylistDTO | 'new' | null>(null);

  const query = toPlaylistsQuery(filters, page, sorting);
  const result = useQuery<CmsPlaylistsResponse>({
    queryKey: ['cms', 'playlists', query],
    queryFn: () => api.cmsPlaylists({ query }),
    placeholderData: keepPreviousData,
  });

  const rows = result.data?.items ?? [];
  const assigned = rows.reduce((sum, row) => sum + row.total, 0);

  const columns = useMemo<ColumnDef<CmsPlaylistDTO>[]>(
    () => [
      {
        id: 'name',
        header: t('cms:playlists.nameColumn'),
        cell: ({ row }) => (
          <span className="flex items-center gap-3">
            {row.original.imageUrl ? (
              <img src={row.original.imageUrl} alt="" className="size-8 rounded-md object-cover" />
            ) : (
              <span
                aria-hidden="true"
                className="size-8 rounded-md"
                style={{ background: swatch(row.original.id) }}
              />
            )}
            <span className="min-w-0">
              <span className="block truncate font-semibold text-ink">{row.original.name.es}</span>
              <span className="block truncate text-[12px] text-muted">{row.original.name.en}</span>
            </span>
          </span>
        ),
      },
      {
        id: 'kind',
        header: t('cms:playlists.kind'),
        cell: ({ row }) => (
          <Badge tone={row.original.kind === 'artist' ? 'cyan' : 'neutral'}>
            {t(`cms:playlists.kind_${row.original.kind}`)}
          </Badge>
        ),
      },
      {
        id: 'songs',
        header: t('cms:playlists.songs'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatNumber(row.original.total, locale)}
            {row.original.publishedTotal !== row.original.total && (
              <span className="ml-1 text-[12px] text-muted">
                ({formatNumber(row.original.publishedTotal, locale)} {t('cms:status.published')})
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('cms:playlists.status'),
        cell: ({ row }) => (
          <Badge tone={row.original.status === 'published' ? 'ok' : 'neutral'}>
            {t(`cms:status.${row.original.status}`)}
          </Badge>
        ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<CmsPlaylistDTO>[] = [
    { header: 'id', value: (p) => p.id },
    { header: 'name_es', value: (p) => p.name.es },
    { header: 'name_en', value: (p) => p.name.en },
    { header: 'kind', value: (p) => p.kind },
    { header: 'status', value: (p) => p.status },
    { header: 'songs', value: (p) => p.total },
    { header: 'published_songs', value: (p) => p.publishedTotal },
  ];

  const update = (patch: Partial<PlaylistFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('cms:playlists.title')}
        /**
         * No count until the query answers: `total ?? 0` claimed an empty collection for the
         * seconds the first page takes (QA 2026-09-15 UX-502, the UX-201 rule applied to the
         * screens it was missed on). An error degrades the count to "—".
         */
        subtitle={
          result.data !== undefined
            ? t('cms:playlists.subtitle', {
                total: formatNumber(result.data.total, locale),
                songs: formatNumber(assigned, locale),
              })
            : result.isError
              ? t('cms:playlists.subtitle', { total: '—', songs: '—' })
              : t('cms:playlists.subtitleLoading')
        }
        actions={
          canEdit ? (
            <Button onClick={() => setSelected('new')}>+ {t('cms:playlists.new')}</Button>
          ) : null
        }
      />

      <div className="px-6 pb-8">
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          label={t('cms:playlists.title')}
          total={result.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={setSelected}
          rowLabel={(row) => t('cms:table.openRow', { name: row.name.es || row.name.en })}
          sortableIds={SORT_IDS}
          selectedRowId={selected && selected !== 'new' ? selected.id : null}
          csv={{
            filename: 'akapella-playlists.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsPlaylists({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <Input
                className="w-64"
                placeholder={t('cms:playlists.searchPlaceholder')}
                aria-label={t('cms:playlists.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-status">{t('cms:playlists.status')}</Label>
                <Select
                  id="f-status"
                  className="w-40"
                  value={filters.status}
                  onChange={(e) => update({ status: e.target.value as PublishStatus | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="draft">{t('cms:status.draft')}</option>
                  <option value="published">{t('cms:status.published')}</option>
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-kind">{t('cms:playlists.kind')}</Label>
                <Select
                  id="f-kind"
                  className="w-40"
                  value={filters.kind}
                  onChange={(e) => update({ kind: e.target.value as PlaylistKind | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  <option value="editorial">{t('cms:playlists.kind_editorial')}</option>
                  <option value="artist">{t('cms:playlists.kind_artist')}</option>
                </Select>
              </span>
              <Button variant="link" size="sm" onClick={() => update(EMPTY_PLAYLIST_FILTERS)}>
                {t('cms:songs.clearFilters')}
              </Button>
            </>
          }
        />
      </div>

      {selected && canEdit && (
        <PlaylistDrawer
          key={selected === 'new' ? 'new' : `${selected.id}:${selected.updatedAt}`}
          playlist={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
